import { Box, Button, Flex, Grid, Text } from "@chakra-ui/react";
import { useState } from "react";
import * as z from "zod";
import {
  DialogActionTrigger,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "@/ui/chakra/dialog";
import { formatDate } from "@/utils/formatDate";
import type { ProductState } from "@/core/products/components/ProductsTable";
import { voucherSchema } from "../schemas/voucherSchema";

type VoucherValues = z.infer<typeof voucherSchema>;

interface VoucherSummaryDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  values: VoucherValues;
  deliverName: string;
  products: ProductState[];
  total: string;
}

const MAX_PRODUCTS_SHOWN = 6;

const SummaryItem = ({
  label,
  value,
}: {
  label: string;
  value?: string | null;
}) => (
  <Box minW={0}>
    <Text fontSize="xs" color="fg.muted" textTransform="uppercase">
      {label}
    </Text>
    <Text fontWeight="medium" truncate>
      {value || "—"}
    </Text>
  </Box>
);

export const VoucherSummaryDialog = ({
  isOpen,
  onClose,
  onConfirm,
  values,
  deliverName,
  products,
  total,
}: VoucherSummaryDialogProps) => {
  const [confirming, setConfirming] = useState(false);

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      await onConfirm();
    } finally {
      setConfirming(false);
    }
  };

  const shown = products.slice(0, MAX_PRODUCTS_SHOWN);
  const remaining = products.length - shown.length;

  return (
    <DialogRoot
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      size="lg"
      role="alertdialog"
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Revisa el vale antes de crearlo</DialogTitle>
        </DialogHeader>
        <DialogCloseTrigger />
        <DialogBody display="flex" flexDirection="column" gap={5}>
          <Grid templateColumns={{ base: "1fr", sm: "1fr 1fr" }} gap={4}>
            <SummaryItem
              label="Tipo de vale"
              value={values.voucherType?.label}
            />
            <SummaryItem
              label="Fecha"
              value={
                values.voucherDate
                  ? formatDate(values.voucherDate.toString())
                  : undefined
              }
            />
            <SummaryItem label="Bodega" value={values.storage?.label} />
            <SummaryItem label="Empresa que entrega" value={deliverName} />
            <SummaryItem
              label="Solicitante"
              value={values.productApplicant?.label}
            />
            <SummaryItem
              label="Empresa mandante"
              value={values.clientEntity?.label}
            />
            <SummaryItem
              label="Orden de trabajo"
              value={values.workOrder?.label ?? "Sin OT"}
            />
            <SummaryItem
              label="Centro de costo"
              value={values.costCenter?.label}
            />
            <SummaryItem
              label="Actividad"
              value={values.activityLabel?.label}
            />
            {values.equipmentInstance?.label && (
              <SummaryItem
                label="Equipo"
                value={values.equipmentInstance.label}
              />
            )}
          </Grid>

          <Box borderWidth="1px" borderColor="border" rounded="lg">
            <Flex
              px={3}
              py={2}
              bg="bg.subtle"
              justify="space-between"
              fontSize="sm"
              fontWeight="semibold"
            >
              <Text>
                {products.length}{" "}
                {products.length === 1 ? "producto" : "productos"}
              </Text>
              <Text>Total {total}</Text>
            </Flex>
            {shown.map((product) => (
              <Flex
                key={product.data.id}
                px={3}
                py={2}
                gap={3}
                fontSize="sm"
                borderTopWidth="1px"
                borderColor="border"
              >
                <Text color="fg.muted" whiteSpace="nowrap">
                  {product.data.externalProduct?.code ||
                    product.data.erpProduct?.erpCode}
                </Text>
                <Text flex={1} truncate>
                  {product.data.description}
                </Text>
                <Text whiteSpace="nowrap">
                  {product.amount} {product.data.unit}
                </Text>
              </Flex>
            ))}
            {remaining > 0 && (
              <Text
                px={3}
                py={2}
                fontSize="sm"
                color="fg.muted"
                borderTopWidth="1px"
                borderColor="border"
              >
                y {remaining} más
              </Text>
            )}
          </Box>

          {values.comment && (
            <SummaryItem label="Comentario" value={values.comment} />
          )}
        </DialogBody>
        <DialogFooter>
          <DialogActionTrigger asChild>
            <Button variant="outline" disabled={confirming}>
              Volver a editar
            </Button>
          </DialogActionTrigger>
          <Button
            colorPalette="brand"
            onClick={handleConfirm}
            loading={confirming}
            autoFocus
          >
            Confirmar y crear
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
};
